import { call, put, takeLatest, all, delay, putResolve, select } from "redux-saga/effects";
import setCrashAnalytics from "@microdoing/shared/utils/crashlytics";

import {
  LOGIN_REQUESTING,
  LOGIN_SUCCESS,
  LOGIN_ERROR,
  LOGOUT,
  RESET_PASSWORD_SUCCESS,
  FETCH_RESET_PASSWORD,
  RESET_PASSWORD_ERROR,
  LOGOUT_STARTING,
} from "./constants";

import { SIGNUP_INPUT_CHANGE } from "../signup/constants";
import { fetchApi } from "../api";

import { setUser } from "../user/actions";
import { setToken } from "../token/actions";
import { setClient } from "../client/actions";

import { navigate, reset } from "../navigation/navigatorHelper";

import socket from "../socket";
import { changeUserLanguage, showAlert } from "../profileSettings/actions";
import i18n from "../../i18n";
import { getActiveCoachingSessionRequest } from "@microdoing/shared/services/coaching/actions";
import { cancelAllNotifications } from "../notifService";
import { getCurrentUserCampaignRequest } from "../userCampaign/actions";
import { getOnboardingSurveyRequest } from "../survey/actions";
import { getUserId } from "../user/selectors";

const endPoints = {
  login: "/v1/auth/login",
  loginWithFacebook: "/v1/auth/facebook",
  resetPassword: "/v1/auth/reset-password",
};

type loginData = {
  email: string;
  password: string;
};

const loginApi = (data: loginData) => {
  return fetchApi(endPoints.login, data, "post");
};

const loginWithFacebookApi = data => {
  return fetchApi(endPoints.loginWithFacebook, data, "post");
};

const resetPasswordApi = (data: { email: string }) => {
  return fetchApi(endPoints.resetPassword, data, "post");
};

function* resetPasswordFlow(action) {
  try {
    const data = action.payload;
    yield call(resetPasswordApi, data);

    yield put({ type: RESET_PASSWORD_SUCCESS });
    yield put(
      showAlert({
        title: i18n.t("password-reset-sent"),
        message: "",
      }),
    );
  } catch (error: any) {
    console.log(error);
    yield put({ type: RESET_PASSWORD_ERROR, payload: error.message });
  }
}

function* loginFlow(action) {
  try {
    const data = action.payload;
    const { route = "Home" } = data;

    const result = yield call(loginApi, data);
    const { user, token, client } = result;

    // inform Redux to set our client token, this is non blocking so...
    yield put(setToken(token));
    yield put(setClient(client));

    yield put(setUser(user));

    yield put({ type: LOGIN_SUCCESS });

    const userId = user._id;
    socket.emit("init", userId);

    if (!user.hasCompleteOnboarding) {
      yield put({ type: SIGNUP_INPUT_CHANGE, payload: user });

      if (user.accessCode)
        yield put(getOnboardingSurveyRequest({ accessCode: user.accessCode }));

      return navigate("SignupSetupName");
    }

    yield put(getCurrentUserCampaignRequest({ userId }));
    yield put(getActiveCoachingSessionRequest({ userId }));
    setCrashAnalytics("Login", { email: user.email, userId });

    yield put(changeUserLanguage(user.language));
    reset({
      index: 0,
      routes: [{ name: "Main" }],
    });
  } catch (error) {
    // if the api call fails, it will "put" the LOGIN_ERROR
    // into the dispatch along with the error.
    console.log(error);
    yield put({ type: LOGIN_ERROR, error, payload: error });
  }
}

function* logoutFlow(action) {
  try {
    const { userId } = action.payload;
    // Emit a logout event for changing user socket
    yield put({ type: LOGOUT });
    const newUserId = yield select(getUserId);


    cancelAllNotifications();

    navigate({
      index: 0,
      routes: [{ name: "Welcome" }],
    });

    socket.emit("logout", userId);

    yield delay(1000);
    socket.connect();

  } catch (e) {
    console.log(e);
  }
}

function* loginWatcher() {
  yield all([
    takeLatest(LOGIN_REQUESTING, loginFlow),
    takeLatest(LOGOUT_STARTING, logoutFlow),
    takeLatest(FETCH_RESET_PASSWORD, resetPasswordFlow),
  ]);
}

export default loginWatcher;
